import { IconButton } from "@mui/material";
import { CloseCircle } from "iconsax-react";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import {
  createContext,
  useReducer,
  ReactElement,
  useContext,
  useEffect,
} from "react";
import websocketReducer from "store/reducers/websocket";
import { useFirebaseContext } from "./FirebaseContext";
import { initialState } from "store/reducers/websocket";
import {
  SET_ORDER_PNL,
  TOGGLE_ORDER_STATUS,
  TOGGLE_USER_LOGIN_STATUS,
} from "store/reducers/actions";
import playSound from "utils/play-sound";
import { WebsocketContextType } from "types/websocket";
import { MimicServerSocket } from "websocket/config";
import { OrderPnl } from "types/order";
const WebSocketContext = createContext<WebsocketContextType | null>(null);

export const WebsocketContextProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { isLoggedIn } = useFirebaseContext();
  const [state, dispatch] = useReducer(websocketReducer, initialState);

  const handleNotification = (message: any) => {
    try {
      if (message) {
        const parsedBody = JSON.parse(message.body);
        if (parsedBody.type === "login" || parsedBody.type === "logout") {
          dispatch({ type: TOGGLE_USER_LOGIN_STATUS });
          enqueueSnackbar(parsedBody.message, {
            autoHideDuration: 9000,
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            action: (key) => (
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => closeSnackbar(key)}
              >
                <CloseCircle fontSize="small" />
              </IconButton>
            ),
          });
          parsedBody.type === "login" ? playSound("profit") : playSound("loss");
        } else if (
          parsedBody.type === "orderCreated" ||
          parsedBody.type === "orderPlaced" ||
          parsedBody.type === "orderModified"
        ) {
          dispatch({ type: TOGGLE_ORDER_STATUS });
          playSound("fresh");
        }
      }
    } catch (error) {
      console.error("Error parsing message body:", error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      MimicServerSocket.subscribe("/topic/notification", handleNotification);
    }

    return () => {
      MimicServerSocket.unsubscribe("/topic/notification");
    };
  }, [isLoggedIn]);

  const handleMessageOrderPnl = (message: any) => {
    try {
      if (message) {
        const parsedBody = JSON.parse(message.body);
        dispatch({
          type: SET_ORDER_PNL,
          payload: {
            orderPnl: parsedBody as OrderPnl,
          },
        });
      }
    } catch (error) {
      console.error("Error parsing message body:", error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      MimicServerSocket.subscribe("/topic/order-pnl", handleMessageOrderPnl);
    }
    return () => {
      MimicServerSocket.unsubscribe("/topic/order-pnl");
    };
  }, [isLoggedIn]);

  return (
    <WebSocketContext.Provider
      value={{
        ...state,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebsocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      "useWebsocketContext must be used within a WebsocketContextProvider"
    );
  }
  return context;
};

export default WebSocketContext;
